import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function ProfileTab({ specializations, slug }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    api.get('/dashboard/profile').then(r => setProfile(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put('/dashboard/profile', {
        name: profile.name, tagline: profile.tagline, qualifications: profile.qualifications,
        affiliations: profile.affiliations,
        registration_number: profile.registration_number,
        specializations: profile.specializations, experience_years: profile.experience_years,
        bio: profile.bio, photo_url: profile.photo_url, phone: profile.phone, consultation_fee: profile.consultation_fee,
        work_pincode: profile.work_pincode,
        gbp_maps_url: profile.gbp_maps_url,
        gender: profile.gender,
      });
      setProfile(data);
      setMsg('Profile saved!');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Error: ' + formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadMsg('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/dashboard/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(p => ({ ...p, photo_url: data.photo_url }));
      setUploadMsg(`Optimized: ${data.original_size_kb}KB → ${data.optimized_size_kb}KB`);
      setTimeout(() => setUploadMsg(''), 5000);
    } catch (err) {
      setUploadMsg('Upload failed. Try a different image.');
    } finally { setUploading(false); }
  };

  if (!profile) return <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>;

  const update = (key, val) => setProfile(p => ({ ...p, [key]: val }));
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const photoSrc = profile.photo_url?.startsWith('/api/uploads') ? `${BACKEND}${profile.photo_url}` : profile.photo_url;

  return (
    <div className="space-y-4">
      <RedeemCouponCard tier={profile?.tier} onRedeemed={(info) => {
        setProfile(p => ({ ...p, tier: info.tier, premium_expires_at: info.new_expiry }));
        setMsg(info.message);
        setTimeout(() => setMsg(''), 4000);
      }} />
    <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-5" data-testid="profile-tab">
      {slug && <ShareUrlBar slug={slug} />}
      <PushNotificationToggle />
      <PinSettings />
      <VoiceAuthenticityNotice />
      <VoiceMeter section="bio" heading="About Me — in your voice" />
      <VoiceMeter section="faq" heading="FAQ — what your patients ask" />

      {/* Photo Upload */}
      <div className="flex items-start gap-5" data-testid="photo-upload-section">
        <div className="flex-shrink-0">
          {photoSrc ? (
            <img src={photoSrc} alt="Profile" className="w-24 h-24 rounded-2xl object-cover border-2 border-slate-200"
              onError={(e) => { e.target.style.display = 'none'; }} data-testid="profile-photo-preview" />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
              <Camera size={24} className="text-slate-400" />
            </div>
          )}
        </div>
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-700">Profile Photo</p>
          <p className="text-[10px] text-slate-500">Upload from your phone or computer. Auto-optimized for fast loading.</p>
          <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="photo-file-input" />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="rounded-full px-4 py-1.5 bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid="upload-photo-button">
            {uploading ? <><Loader2 size={12} className="animate-spin" /> Optimizing...</> : <><Upload size={12} /> Upload Photo</>}
          </button>
          {uploadMsg && <p className="text-[10px] text-emerald-600 font-medium">{uploadMsg}</p>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full Name" value={profile.name} onChange={v => update('name', v)} testid="profile-name" />
        <Field label="Tagline" value={profile.tagline} onChange={v => update('tagline', v)} placeholder="e.g. Compassionate care for your heart" testid="profile-tagline" />
        <Field label="Qualifications" value={profile.qualifications} onChange={v => update('qualifications', v)} placeholder="MBBS, MD, etc." testid="profile-quals" />
        <div className="md:col-span-2">
          <Field
            label="Affiliations"
            value={profile.affiliations}
            onChange={v => update('affiliations', v)}
            placeholder="e.g. Consultant Cardiologist, HeartCare Clinic"
            testid="profile-affiliations"
          />
          <p className="text-[11px] text-slate-500 mt-1">Shown as a sub-heading under your name on your website, your saved-contact page, the clinic check-in page and the QR poster.</p>
        </div>
        <Field label="NMC / State Medical Council Registration No." value={profile.registration_number} onChange={v => update('registration_number', v)} placeholder="e.g. 12345 (NMC or State Council)" testid="profile-reg-number" />
        <Field label="Experience (years)" value={profile.experience_years} onChange={v => update('experience_years', parseInt(v) || 0)} type="number" testid="profile-exp" />
        <Field label="Phone" value={profile.phone} onChange={v => update('phone', v)} testid="profile-phone" />
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">Gender <span className="text-xs text-slate-400 font-normal">(used for the avatar shown when no photo is uploaded)</span></label>
          <select value={profile.gender || ''} onChange={e => update('gender', e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-sky-400" data-testid="profile-gender">
            <option value="">Prefer not to say</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <Field label="Consultation Fee (Rs.)" value={profile.consultation_fee} onChange={v => update('consultation_fee', v)} testid="profile-fee" />
        <Field label="Work Pincode" value={profile.work_pincode} onChange={v => update('work_pincode', v)} placeholder="Primary area of practice" testid="profile-work-pincode" />

        {/* Google Business Profile — feeds the TQS auto-review deep-link */}
        <div className="md:col-span-2 mt-2 bg-emerald-50/60 border border-emerald-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Star size={14} className="text-emerald-600 fill-emerald-600" />
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Google Maps Clinic Link (boosts ranking massively)</p>
          </div>
          <p className="text-[11px] text-slate-600 mb-3">Paste your clinic's Google Maps link below — that's it. We'll auto-build a 1-tap "Leave a review" link for every patient survey. <b>How to copy it:</b> open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">Google Maps</a> → search your clinic by name → tap <b>Share</b> → <b>Copy link</b> → paste here. No technical setup needed.</p>
          <Field
            label="Google Maps link"
            value={profile.gbp_maps_url}
            onChange={v => update('gbp_maps_url', v)}
            placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
            testid="profile-gbp-maps-url"
          />
          {profile.gbp_review_url && (
            <p className="text-[11px] text-emerald-700 font-medium mt-2 flex items-center gap-1.5" data-testid="profile-gbp-resolved">
              <CheckCircle size={12} /> Auto-detected. Patients will be sent here to leave you a Google review:
              <a href={profile.gbp_review_url} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[280px]">{profile.gbp_review_url}</a>
            </p>
          )}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Specializations</label>
        <div className="flex flex-wrap gap-2">
          {specializations.map(s => (
            <button key={s.slug} onClick={() => {
              const specs = profile.specializations || [];
              update('specializations', specs.includes(s.slug) ? specs.filter(x => x !== s.slug) : [...specs, s.slug]);
            }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${(profile.specializations || []).includes(s.slug) ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`spec-${s.slug}`}>
              {s.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Bio</label>
        <textarea className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none resize-none"
          rows={4} value={profile.bio || ''} onChange={e => update('bio', e.target.value)} data-testid="profile-bio" />
      </div>

      {/* Payment-privacy toggle (Feb 2026 spec). When false, the ₹ amount
          column in Patient Log is hidden — useful on shared consult-room
          screens. The Amounts tab always shows the figures regardless. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Show amount column in Patient Log</p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">Some doctors prefer to hide ₹ figures from the Patient Log on shared screens. The <b>Amounts</b> tab always shows your full revenue privately.</p>
          </div>
          <button
            type="button"
            onClick={() => update('show_amount_in_patient_log', !(profile.show_amount_in_patient_log !== false))}
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${profile.show_amount_in_patient_log !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
            data-testid="profile-show-amount-toggle"
            aria-pressed={profile.show_amount_in_patient_log !== false}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${profile.show_amount_in_patient_log !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="rounded-full px-6 py-2.5 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="save-profile-button">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Profile
        </button>
        {msg && <span className={`text-sm ${msg.startsWith('Error') ? 'text-red-600' : 'text-emerald-600'}`}>{msg}</span>}
      </div>
      <ChangePasswordSection />
    </div>
    </div>
  );
}

